import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { getDashboardSummary } from "../../services/dashboard_service";
import { fetchMyFleetLive } from "../../services/fleetx_service";
import "./AdminDashboard.css";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
const URGENCY_DAYS = {
    danger: 7,
    warning: 30,
};

const ASSIGNMENTS_PAGE_SIZE = 10;
const BRANCH_COLORS = [
    "#2149c7",
    "#0d8b7d",
    "#dd8b2e",
    "#6a4bc4",
    "#c13c33",
    "#157a5c",
];
const FUEL_COLORS = ["#2149c7", "#0d8b7d", "#dd8b2e", "#6a4bc4", "#c13c33"];
const TRANSMISSION_COLORS = ["#10b981", "#3730e0", "#f59e0b", "#8b5cf6"];
const MAP_POLL_MS = 20000;
const TREND_DAYS = 14;

function getDaysRemaining(dateStr) {
    if (!dateStr) return null;

    const target = new Date(dateStr);
    if (Number.isNaN(target.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function getUrgencyLevel(dateStr) {
    const days = getDaysRemaining(dateStr);

    if (days === null) return "unknown";
    if (days < 0) return "expired";
    if (days <= URGENCY_DAYS.danger) return "danger";
    if (days <= URGENCY_DAYS.warning) return "warning";
    return "ok";
}

function formatUrgencyLabel(dateStr) {
    const days = getDaysRemaining(dateStr);

    if (days === null) return "";
    if (days < 0) return `Expired ${Math.abs(days)}d ago`;
    if (days === 0) return "Expires today";
    if (days === 1) return "Expires tomorrow";
    return `Expires in ${days}d`;
}
function buildAssignmentsTrend(assignments, days = TREND_DAYS) {
    const buckets = new Map();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        buckets.set(key, {
            date: key,
            label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            count: 0,
        });
    }

    assignments.forEach((item) => {
        if (!item.assigned_date) return;
        const d = new Date(item.assigned_date);
        if (Number.isNaN(d.getTime())) return;
        d.setHours(0, 0, 0, 0);
        const key = d.toISOString().slice(0, 10);
        if (buckets.has(key)) {
            buckets.get(key).count += 1;
        }
    });

    return Array.from(buckets.values());
}

export default function AdminDashboard() {
    const navigate = useNavigate();

    const [dashboard, setDashboard] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [assignmentsPage, setAssignmentsPage] = useState(1);

    const isFetchingRef = useRef(false);
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        fetchDashboard();
        const interval = setInterval(() => {
            fetchDashboard();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const fetchDashboard = async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        if (!hasLoadedOnceRef.current) {
            setInitialLoading(true);
        } else {
            setRefreshing(true);
        }
        setErrorMsg("");

        try {
            const data = await getDashboardSummary();
            setDashboard(data);
            hasLoadedOnceRef.current = true;
        } catch (error) {
            console.error("Dashboard Error:", error);
            if (!hasLoadedOnceRef.current) {
                setErrorMsg("Couldn't load the dashboard. Please try again.");
            }
        } finally {
            setInitialLoading(false);
            setRefreshing(false);
            isFetchingRef.current = false;
        }
    };

    const summary = useMemo(() => dashboard?.summary || {}, [dashboard]);
    const recentAssignments = useMemo(
        () => dashboard?.recent_assignments || [],
        [dashboard]
    );
    const licenseExpiry = useMemo(
        () => dashboard?.license_expiry || [],
        [dashboard]
    );
    const outstandingSuppliers = useMemo(
        () => dashboard?.outstanding_suppliers || [],
        [dashboard]
    );
    const fleetByFuel = useMemo(() => dashboard?.fleet_by_fuel || [], [dashboard]);
    const fleetByTransmission = useMemo(
        () => dashboard?.fleet_by_transmission || [],
        [dashboard]
    );
    const branchBreakdown = useMemo(
        () => dashboard?.branch_breakdown || [],
        [dashboard]
    );

    const cards = useMemo(
        () => [
            {
                title: "Drivers",
                value: summary.total_drivers ?? 0,
                color: "#3730e0",
                to: "/drivers",
            },
            {
                title: "Vehicles",
                value: summary.total_vehicles ?? 0,
                color: "#10b981",
                to: "/vehicles",
            },
            {
                title: "Suppliers",
                value: summary.total_suppliers ?? 0,
                color: "#8b5cf6",
                to: "/suppliers",
            },
            {
                title: "Allotments",
                value: summary.total_allotments ?? 0,
                color: "#f59e0b",
                to: "/vehicle-assignment",
            },
            {
                title: "Staff",
                value: summary.total_staff ?? 0,
                color: "#0ea5e9",
                to: "/staff",
            },
            {
                title: "Outstanding",
                value: formatLakhs(summary.outstanding_amount),
                color: "#14b8a6",
                to: "/suppliers?dues=has_dues",
            },
        ],
        [summary]
    );

    const assignmentsTrend = useMemo(
        () => buildAssignmentsTrend(recentAssignments),
        [recentAssignments]
    );

    const branchVehicleShare = useMemo(
        () =>
            branchBreakdown
                .map((b) => ({
                    label: b.label || "Unassigned",
                    count: Number(b.assigned_vehicle_count || 0),
                }))
                .filter((b) => b.count > 0),
        [branchBreakdown]
    );

    useEffect(() => {
        setAssignmentsPage(1);
    }, [recentAssignments.length]);

    const assignmentsTotalPages = Math.max(
        1,
        Math.ceil(recentAssignments.length / ASSIGNMENTS_PAGE_SIZE)
    );
    const paginatedAssignments = recentAssignments.slice(
        (assignmentsPage - 1) * ASSIGNMENTS_PAGE_SIZE,
        assignmentsPage * ASSIGNMENTS_PAGE_SIZE
    );
    const goToAssignmentsPage = (page) => {
        setAssignmentsPage(Math.min(Math.max(1, page), assignmentsTotalPages));
    };

    if (initialLoading && !dashboard) {
        return (
            <DashboardShell>
                <div className="dashboard-state">Loading dashboard…</div>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
            {errorMsg && (
                <div className="banner banner-error">
                    <span>{errorMsg}</span>
                    <button
                        type="button"
                        onClick={() => fetchDashboard()}
                    >
                        Retry
                    </button>
                </div>
            )}

            {refreshing && (
                <div className="refresh-indicator" aria-live="polite">
                    Refreshing…
                </div>
            )}

            <div className="stats-grid">
                {cards.map((card) => (
                    <button
                        type="button"
                        key={card.title}
                        className="dash-stat-card"
                        style={{
                            borderTop: `2px solid ${card.color}`,
                            borderLeft: `2px solid ${card.color}`,
                        }}
                        onClick={() => navigate(card.to)}
                    >
                        <h4>{card.title}</h4>
                        <h5>{card.value}</h5>
                    </button>
                ))}
            </div>

            <div className="dashboard-box assignments-box">
                <div className="box-header">
                    <h3>Recent Assignments</h3>
                    <button
                        type="button"
                        className="link-btn"
                        onClick={() => navigate("/vehicles?assignment=assigned")}
                    >
                        View all
                    </button>
                </div>

                {recentAssignments.length ? (
                    <>
                        <div className="assignments-scroll">
                            <table className="dashboard-table">
                                <thead>
                                    <tr>
                                        <th>Vehicle</th>
                                        <th>Make / Model</th>
                                        <th>Driver / Supplier</th>
                                        <th>Mobile</th>
                                        <th>Date</th>
                                        <th>Status</th>

                                    </tr>
                                </thead>

                                <tbody>
                                    {paginatedAssignments.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.vehicle_registration_number}</td>
                                            <td>
                                                {[item.vehicle_make, item.vehicle_model]
                                                    .filter(Boolean)
                                                    .join(" ") || "-"}
                                            </td>
                                            <td>{item.full_name || item.supplier_name || "-"}</td>
                                            <td>{item.driver_mobile || item.supplier_mobile || "-"}</td>
                                            <td>
                                                {item.assigned_date
                                                    ? new Date(
                                                        item.assigned_date
                                                    ).toLocaleDateString()
                                                    : "-"}
                                            </td>
                                            <td>
                                                <span
                                                    className={`assignment-status status-${item.is_active ? "active" : "inactive"
                                                        }`}
                                                >
                                                    {item.is_active ? "Active" : "Inactive"}
                                                </span>
                                            </td>

                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {recentAssignments.length > ASSIGNMENTS_PAGE_SIZE && (
                            <div className="mini-pagination">
                                <span className="mini-pagination-count">
                                    Showing{" "}
                                    <strong>
                                        {(assignmentsPage - 1) * ASSIGNMENTS_PAGE_SIZE + 1}
                                    </strong>
                                    –
                                    <strong>
                                        {Math.min(
                                            assignmentsPage * ASSIGNMENTS_PAGE_SIZE,
                                            recentAssignments.length
                                        )}
                                    </strong>{" "}
                                    of <strong>{recentAssignments.length}</strong>
                                </span>
                                <div className="mini-pagination-controls">
                                    <button
                                        type="button"
                                        className="pagination-nav-btn"
                                        onClick={() => goToAssignmentsPage(assignmentsPage - 1)}
                                        disabled={assignmentsPage === 1}
                                    >
                                        ‹ Prev
                                    </button>
                                    <span className="pagination-page-label">
                                        Page {assignmentsPage} of {assignmentsTotalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="pagination-nav-btn"
                                        onClick={() => goToAssignmentsPage(assignmentsPage + 1)}
                                        disabled={assignmentsPage === assignmentsTotalPages}
                                    >
                                        Next ›
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <EmptyState message="No recent assignments." />
                )}
            </div>

            <div className="two-col-grid">
                <ExpiryBox
                    title="License Expiry"
                    items={licenseExpiry}
                    getLabel={(item) => item.full_name}
                    getDate={(item) => item.license_expiry}
                    emptyMessage="No upcoming license expiries."
                    onViewAll={() => navigate("/drivers?alert=license_expiry")}
                />

                <div className="dashboard-box">
                    <div className="box-header">
                        <h3>Outstanding Suppliers</h3>
                        {outstandingSuppliers.length > 0 && (
                            <button
                                type="button"
                                className="link-btn"
                                onClick={() => navigate("/suppliers?dues=has_dues")}
                            >
                                View all
                            </button>
                        )}
                    </div>

                    {outstandingSuppliers.length ? (
                        outstandingSuppliers.map((item, index) => (
                            <div className="alert-item" key={item.id ?? index}>
                                <span>{item.supplier_name}</span>
                                <strong>
                                    ₹{Number(item.outstanding_amount || 0).toLocaleString(
                                        "en-IN"
                                    )}
                                </strong>
                            </div>
                        ))
                    ) : (
                        <EmptyState message="No outstanding amounts." />
                    )}
                </div>
            </div>

            <div className="charts-grid">
                <div className="dashboard-box triple-donut-box">
                    <div className="box-header">
                        <h3>Fleet Composition</h3>
                    </div>
                    <div className="compact-donuts-row">
                        <CompactDonut
                            title="By Fuel Type"
                            data={fleetByFuel}
                            colors={FUEL_COLORS}
                        />
                        <CompactDonut
                            title="By Transmission"
                            data={fleetByTransmission}
                            colors={TRANSMISSION_COLORS}
                        />
                        <CompactDonut
                            title="By Branch"
                            data={branchVehicleShare}
                            colors={BRANCH_COLORS}
                        />
                    </div>
                </div>

                <div className="dashboard-box trend-box">
                    <div className="box-header">
                        <h3>Assignments — Last {TREND_DAYS} Days</h3>
                    </div>
                    <AssignmentsTrendChart data={assignmentsTrend} />
                </div>
            </div>

            <BranchBreakdownBox data={branchBreakdown} />

            <VehicleMapBox navigate={navigate} />
        </DashboardShell>
    );
}

function DashboardShell({ children }) {
    return (
        <div className="admin-layout">
            <Sidebar />

            <div className="admin-main">
                <Header />

                <div className="dashboard-container">{children}</div>
            </div>
        </div>
    );
}

function ExpiryBox({ title, items, getLabel, getDate, emptyMessage, onViewAll }) {
    return (
        <div className="dashboard-box">
            <div className="box-header">
                <h3>{title}</h3>
                {items.length > 0 && (
                    <button type="button" className="link-btn" onClick={onViewAll}>
                        View all
                    </button>
                )}
            </div>

            {items.length ? (
                items.map((item, index) => {
                    const dateStr = getDate(item);
                    const urgency = getUrgencyLevel(dateStr);

                    return (
                        <div className="alert-item" key={item.id ?? index}>
                            <span>{getLabel(item)}</span>

                            <div className="alert-meta">
                                <strong>
                                    {dateStr
                                        ? new Date(dateStr).toLocaleDateString()
                                        : "-"}
                                </strong>

                                {urgency !== "unknown" && urgency !== "ok" && (
                                    <span className={`urgency-badge urgency-${urgency}`}>
                                        {formatUrgencyLabel(dateStr)}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })
            ) : (
                <EmptyState message={emptyMessage} />
            )}
        </div>
    );
}

function ChartTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0];
    return (
        <div
            style={{
                background: "#ffffff",
                border: "1px solid #e2e5ec",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 12,
                fontFamily: "Inter, sans-serif",
                boxShadow: "0 8px 20px rgba(16, 19, 32, 0.08)",
            }}
        >
            <strong>{entry.name}</strong>: {entry.value}
        </div>
    );
}

function CompactDonut({ title, data, colors }) {
    const chartData = data
        .map((d) => ({
            name: d.label || "Unspecified",
            value: Number(d.count || 0),
        }))
        .filter((d) => d.value > 0);

    const total = chartData.reduce((sum, d) => sum + d.value, 0);

    return (
        <div className="compact-donut">
            {title && <h4 className="bar-chart-title">{title}</h4>}

            {chartData.length ? (
                <>
                    <div className="compact-donut-svg">
                        <ResponsiveContainer width="100%" height={100}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={26}
                                    outerRadius={42}
                                    paddingAngle={2}
                                    strokeWidth={0}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={entry.name}
                                            fill={colors[index % colors.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<ChartTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="compact-donut-legend">
                        {chartData.map((entry, index) => (
                            <div className="donut-legend-item" key={entry.name}>
                                <span
                                    className="donut-legend-swatch"
                                    style={{ background: colors[index % colors.length] }}
                                />
                                <span className="donut-legend-label">{entry.name}</span>
                                <span className="donut-legend-value">
                                    {entry.value}
                                    <em>
                                        {" "}
                                        ({Math.round((entry.value / total) * 100)}%)
                                    </em>
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <EmptyState message="No data available." />
            )}
        </div>
    );
}

function AssignmentsTrendChart({ data }) {
    const hasActivity = data.some((d) => d.count > 0);

    if (!hasActivity) {
        return <EmptyState message="No assignment activity in this period." />;
    }

    return (
        <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e5ec" vertical={false} />
                <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "#667085" }}
                    axisLine={{ stroke: "#e2e5ec" }}
                    tickLine={false}
                    interval={Math.ceil(data.length / 7)}
                />
                <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: "#667085" }}
                    axisLine={false}
                    tickLine={false}
                    width={28}
                />
                <Tooltip content={<ChartTooltip />} />
                <Line
                    type="monotone"
                    dataKey="count"
                    name="Assignments"
                    stroke="#2149c7"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#2149c7", strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

function BranchBreakdownBox({ data }) {
    return (
        <div className="dashboard-box branch-box">
            <div className="box-header">
                <h3>Branch-wise Breakdown</h3>
            </div>

            {data.length ? (
                <div className="branch-list">
                    {data.map((b, index) => {
                        const color = BRANCH_COLORS[index % BRANCH_COLORS.length];
                        const driverCount = Number(b.driver_count || 0);
                        const vehicleCount = Number(b.assigned_vehicle_count || 0);
                        const label = b.label || "Unassigned";
                        const initial = label.trim().charAt(0).toUpperCase() || "?";

                        return (
                            <div className="branch-row" key={label ?? index}>
                                <div className="branch-row-top">
                                    <div className="branch-identity">
                                        <span
                                            className="branch-avatar"
                                            style={{
                                                background: `${color}1a`,
                                                color,
                                            }}
                                        >
                                            {initial}
                                        </span>
                                        <span className="branch-name">{label}</span>
                                    </div>

                                    <div className="branch-metrics">
                                        <span className="branch-metric-pill branch-metric-pill--drivers">
                                            {driverCount} <em>drivers</em>
                                        </span>
                                        <span className="branch-metric-pill branch-metric-pill--vehicles">
                                            {vehicleCount} <em>vehicles</em>
                                        </span>
                                    </div>
                                </div>

                            </div>
                        );
                    })}
                </div>
            ) : (
                <EmptyState message="No branch data available." />
            )}
        </div>
    );
}

function VehicleMapBox({ navigate }) {
    const mapRef = useRef(null);
    const containerRef = useRef(null);
    const isFetchingRef = useRef(false);
    const hasLoadedOnceRef = useRef(false);

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLocations = useCallback(async () => {
        if (isFetchingRef.current) return;
        if (document.visibilityState === "hidden") return;
        isFetchingRef.current = true;

        if (!hasLoadedOnceRef.current) setLoading(true);

        try {
            const data = await fetchMyFleetLive(undefined, undefined, {
                liveOnly: true,
                light: true,
            });
            setLocations(data.vehicles || []);
            setError(null);
            hasLoadedOnceRef.current = true;
        } catch (err) {
            if (!hasLoadedOnceRef.current) setError(err.message);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchLocations();
        const interval = setInterval(fetchLocations, MAP_POLL_MS);

        const onVisibility = () => {
            if (document.visibilityState === "visible") fetchLocations();
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [fetchLocations]);

    const validLocations = useMemo(
        () =>
            locations.filter(
                (loc) => typeof loc.latitude === "number" && typeof loc.longitude === "number"
            ),
        [locations]
    );

    const center = validLocations.length
        ? [validLocations[0].latitude, validLocations[0].longitude]
        : [20.5937, 78.9629];

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const fixSize = () => {
            setTimeout(() => map.invalidateSize(), 100);
        };

        window.addEventListener("resize", fixSize);

        let ro;
        if (containerRef.current && "ResizeObserver" in window) {
            ro = new ResizeObserver(fixSize);
            ro.observe(containerRef.current);
        }

        fixSize();

        return () => {
            window.removeEventListener("resize", fixSize);
            if (ro) ro.disconnect();
        };
    }, [validLocations.length]);

    return (
        <div className="dashboard-box map-box">
            <div className="box-header">
                <h3>Live Vehicle Map</h3>
                <button
                    type="button"
                    className="link-btn map-count"
                    onClick={() => navigate("/live-vehicles")}
                >
                    {validLocations.length} vehicle
                    {validLocations.length === 1 ? "" : "s"} tracked
                </button>
            </div>

            {error && !validLocations.length && (
                <EmptyState message={`Couldn't load live locations: ${error}`} />
            )}

            {loading && !hasLoadedOnceRef.current ? (
                <EmptyState message="Loading live locations…" />
            ) : validLocations.length ? (
                <div ref={containerRef} style={{ height: "100%", width: "100%" }}>
                    <MapContainer
                        center={center}
                        zoom={6}
                        scrollWheelZoom={false}
                        className="vehicle-map"
                        whenCreated={(map) => {
                            mapRef.current = map;
                        }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {validLocations.map((loc, index) => (
                            <Marker
                                key={loc.vehicleId || loc.vehicleNumber || index}
                                position={[loc.latitude, loc.longitude]}
                            >
                                <Popup>
                                    <strong>{loc.vehicleNumber}</strong>
                                    <br />
                                    {loc.driverName || "Unassigned"}
                                    <br />
                                    {loc.address || "-"}
                                    <br />
                                    {loc.lastUpdatedAt
                                        ? new Date(Number(loc.lastUpdatedAt)).toLocaleString()
                                        : ""}
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>
            ) : (
                <EmptyState message="No location data available yet." />
            )}
        </div>
    );
}

function EmptyState({ message }) {
    return <p className="empty-state">{message}</p>;
}

function formatLakhs(amount) {
    const value = Number(amount || 0) / 100000;
    return `₹${value.toFixed(1)}L`;
}